import React, {useEffect, useState} from "react";
import {
    Button,
    Dropdown,
    DropdownProps,
    Form,
    Header,
    Table
} from "semantic-ui-react";
import agent from "../../agent";
import {
    ActionLogListRequest, ActionLogListResponse,
    ActionLogModel,
    ActionLogType, ActionLogTypeNames,
    ActionLogUserRoleNames,
} from "./ActionLogModel";

import {Utils} from "../../helpers/Utils";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import PagingFooter from "../common/PagingFooter";
import {hasRole, UserRole} from "../../helpers/UserRole";
import {textLabels} from "../../consts/textLabels";
import {getActionLogUserRoleDisplayName} from "./ActionLogService";
import {TaskModel} from "../workTableTask/TaskModel";
import CreatedTaskDetailsModal from "../workTablePatients/CreatedTaskDetailsModal";

const ActionLogList = () => {
    const styles = { ...commonStyles, ...localStyles };
    const [loading, setLoading] = useState(true);
    const [request, setRequest] = useState<ActionLogListRequest>({
        page: 1,
        limit: 10
    });
    const [response, setResponse] = useState<ActionLogListResponse>();
    const [createdTask, setCreatedTask] = useState<TaskModel>();

    useEffect(() => {
        load().then();
    }, [request]);

    async function load() {
        setLoading(true);
        setResponse(await agent.ActionLogProvider.getList(request));
        setLoading(false);
    }

    async function loadPage(page: number, limit?: number) {
        setRequest({...request, page: page, limit: limit ?? request.limit});
    }

    async function exportActionLogList() {
        let task = await agent.ActionLogProvider.exportActionLogList();
        setCreatedTask(task);
    }

    function buildFilters(): JSX.Element {
        return (<div className={styles.list}><Form>
            <Form.Group>
                {buildUserRoleFilter()}
                {buildTypeFilter()}
                {buildObjectFilter()}
            </Form.Group>
        </Form></div>);
    }

    function buildTypeFilter(): JSX.Element {
        return (<Form.Field width={"3"}>
            <label>
                Тип действия
            </label>
            <Dropdown
                selection
                search={true}
                clearable={true}
                placeholder={textLabels.placeholderMessage}
                noResultsMessage={textLabels.noResultsMessage}
                onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                    setRequest({...request, type: data.value as ActionLogType});
                }}
                options={ActionLogTypeNames}
            />
        </Form.Field>);
    }

    function buildUserRoleFilter(): JSX.Element {
        return (<Form.Field width={"3"}>
            <label>
                Роль
            </label>
            <Dropdown
                selection
                search={true}
                clearable={true}
                placeholder={textLabels.placeholderMessage}
                noResultsMessage={textLabels.noResultsMessage}
                onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                    setRequest({...request, userRole: data.value as UserRole});
                }}
                options={ActionLogUserRoleNames}
            />
        </Form.Field>);
    }

    function buildObjectFilter(): JSX.Element {
        return (
            <Form.Field width={"3"}>
                <label>Объект</label>
                <Form.Input
                    placeholder="Поиск по объекту"
                    value={request.objectSearch || ""}
                    onChange={(e, data) => {
                        setRequest(prev => ({
                            ...prev,
                            objectSearch: data.value || undefined,
                            page: 1
                        }));
                    }}
                />
            </Form.Field>
        );
    }

    return (
        <div className={styles.table} style={{"paddingTop": "32px"}}>

            <div className={styles.header}>
                <Header>Журнал действий</Header>
                {(hasRole(UserRole.ADMIN)) &&
                    <div>
                        <Button
                            inverted
                            className={styles.createButton}
                            size={'large'}
                            color={'green'}
                            onClick={exportActionLogList}
                        >
                            Экспорт журнала действий
                        </Button>
                    </div>
                }
            </div>
            {createdTask && <CreatedTaskDetailsModal task={createdTask}
                                                                onClose={() => setCreatedTask(undefined)}/>}

            {buildFilters()}

            <Table celled selectable striped verticalAlign='middle' color={"teal"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>ФИО</Table.HeaderCell>
                        <Table.HeaderCell>Логин</Table.HeaderCell>
                        <Table.HeaderCell>Роль</Table.HeaderCell>
                        <Table.HeaderCell>Тип действия</Table.HeaderCell>
                        <Table.HeaderCell>Дата и время</Table.HeaderCell>
                        <Table.HeaderCell>Объект</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {response?.docs.map((el, index) =>{
                        return (
                            <Table.Row key={`key-${index}-${el._id}`} style={{cursor: "pointer"}}>
                                <Table.Cell floated={'left'}>
                                    {el.creator.displayName}
                                </Table.Cell>
                                <Table.Cell floated={'left'}>
                                    {el.creator.login}
                                </Table.Cell>
                                <Table.Cell floated={'left'}>
                                    {getActionLogUserRoleDisplayName(el.creator.role)}
                                </Table.Cell>
                                <Table.Cell floated={'left'}>
                                    {el.type.displayName}
                                </Table.Cell>
                                <Table.Cell floated={'left'}>
                                    {Utils.formatDateTime(el.date)}
                                </Table.Cell>
                                <Table.Cell floated={'left'}>
                                    {el.object?.displayName || ""}
                                </Table.Cell>
                            </Table.Row>
                        )
                    }
                    )}
                </Table.Body>

                <Table.Footer>
                    <Table.Row>
                        <Table.HeaderCell colSpan='9'>
                            <PagingFooter page={response?.page}
                                          totalPages={response?.totalPages}
                                          nextPage={response?.nextPage}
                                          prevPage={response?.prevPage}
                                          totalDocs={response?.totalDocs}
                                          limit={response?.limit}
                                          loadPage={loadPage}/>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        </div>);
}

export default ActionLogList;